import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "../../core/components/RecordNotFound";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  UserCheck,
  PlusCircle,
  Edit,
  Trash2,
  Key,
  Eye,
  EyeOff,
  XCircle,
  MoreVertical,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { ReactSelect } from "@/components/ui/react-select";
import { all_routes } from "../../Router/all_routes";

export function AdminsPage() {
  const queryClient = useQueryClient();
  const currentUser = useSelector((state) => state.user_data);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });
  const [rowSelection, setRowSelection] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);

  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = all_routes.signin;
  };

  const [formData, setFormData] = useState({
    id: null,
    employee_id: "",
    username: "",
    password: "",
    is_temp_pass: false,
    status: "active",
    role_id: "",
  });

  const [passwordFormData, setPasswordFormData] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });

  // Queries
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["users", pagination.pageIndex, pagination.pageSize, searchText],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.USERS, {
        params: {
          page: pagination.pageIndex + 1,
          per_page: pagination.pageSize,
          search: searchText,
        },
      });
      return res.data;
    },
  });

  const users = usersData?.data || [];
  const totalCount =
    usersData?.total ?? usersData?.meta?.total ?? users.length ?? 0;
  const pageCount = usersData?.last_page ?? usersData?.meta?.last_page ?? 1;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchText]);

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.EMPLOYEES);
      return res.data.data;
    },
  });

  const { data: roles = [] } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.ROLES);
      return res.data.data;
    },
  });

  // Filter employees who don't have a user record yet (for new user creation)
  const availableEmployees = employees.filter(
    (emp) => !users.some((user) => user.employee?.id === emp.id),
  );

  const employeeOptions = availableEmployees.map((emp) => ({
    value: String(emp.id),
    label: `${emp.full_name} (${emp.email})`,
  }));

  const roleOptions = roles.map((role) => ({
    value: String(role.id),
    label: role.name,
  }));

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // Mutations
  const saveUserMutation = useMutation({
    mutationFn: async (data) => {
      if (isEdit) {
        const payload = {
          status: data.status,
          role: data.role_id ? [data.role_id] : [],
        };
        if (data.password) payload.password = data.password;
        return apiClient.put(
          `${API_ENDPOINTS.USERS.USERS}/${data.id}`,
          payload,
        );
      } else {
        const res = await apiClient.post(API_ENDPOINTS.USERS.USERS, {
          employee_id: data.employee_id,
          username: data.username,
          password: data.password,
          is_temp_pass: data.is_temp_pass,
          status: data.status,
        });

        // If role is selected, assign it after creation
        if (res.data.data?.id && data.role_id) {
          await apiClient.put(
            `${API_ENDPOINTS.USERS.USERS}/${res.data.data.id}`,
            {
              role: [data.role_id],
              status: data.status,
            },
          );
        }
        return res;
      }
    },
    onSuccess: () => {
      toast.success(`User ${isEdit ? "updated" : "created"} successfully!`);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setIsModalOpen(false);
      if (isEdit && currentUser?.id === formData.id) {
        setShowLogoutPopup(true);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to save user");
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`${API_ENDPOINTS.USERS.USERS}/${id}`);
    },
    onSuccess: () => {
      toast.success("User deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setDeleteDialogOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete user");
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.patch(
        `${API_ENDPOINTS.USERS.USERS}/${selectedUserForPassword.id}/change-password`,
        data,
      );
    },
    onSuccess: () => {
      toast.success("Password changed successfully!");
      setIsPasswordModalOpen(false);
      setPasswordFormData({
        current_password: "",
        password: "",
        password_confirmation: "",
      });
      if (currentUser?.id === selectedUserForPassword?.id) {
        setShowLogoutPopup(true);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to change password");
    },
  });

  const handleOpenModal = (user = null) => {
    if (user) {
      setIsEdit(true);
      setFormData({
        id: user.id,
        employee_id: user.employee?.id || "",
        username: user.username,
        password: "",
        is_temp_pass: false,
        status: user.status || "active",
        role_id: user.roles?.[0]?.id || "",
      });
    } else {
      setIsEdit(false);
      setFormData({
        id: null,
        employee_id: "",
        username: "",
        password: "",
        is_temp_pass: false,
        status: "active",
        role_id: "",
      });
    }
    setIsModalOpen(true);
  };

  const handleOpenPasswordModal = (user) => {
    setSelectedUserForPassword(user);
    setIsPasswordModalOpen(true);
  };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "employee",
      header: "Employee",
      cell: ({ row }) => {
        const emp = row.original.employee;
        return <div className="font-medium">{emp?.full_name || "N/A"}</div>;
      },
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => {
        const emp = row.original.employee;
        return <div className="text-sm">{emp?.email || "N/A"}</div>;
      },
    },
    {
      accessorKey: "username",
      header: "Username",
      cell: ({ row }) => (
        <Badge variant="outline">{row.getValue("username")}</Badge>
      ),
    },
    {
      accessorKey: "roles",
      header: "Role",
      cell: ({ row }) => {
        const roles = row.getValue("roles") || [];
        return (
          <div className="flex flex-wrap gap-1">
            {roles.length > 0 ? (
              roles.map((r) => (
                <Badge
                  key={r.id}
                  variant="secondary"
                  className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors"
                >
                  {r.name}
                </Badge>
              ))
            ) : (
              <Badge
                variant="outline"
                className="text-muted-foreground/60 border-muted-foreground/20 border-dashed"
              >
                -
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={
            row.getValue("status") === "active"
              ? "success"
              : row.getValue("status") === "inactive"
                ? "warning"
                : "destructive"
          }
          className="capitalize"
        >
          {row.getValue("status")}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const user = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hasPermission("user update") && (
                <DropdownMenuItem onClick={() => handleOpenModal(user)}>
                  <Edit className="mr-2 h-4 w-4" /> Edit User
                </DropdownMenuItem>
              )}
              {hasPermission("user change_password") && (
                <DropdownMenuItem onClick={() => handleOpenPasswordModal(user)}>
                  <Key className="mr-2 h-4 w-4" /> Change Password
                </DropdownMenuItem>
              )}
              {hasPermission("user delete") && (
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  onClick={() => {
                    setUserToDelete(user);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete Account
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (!hasPermission("user read")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader className="animate-in fade-in slide-in-from-top-4 duration-500">
        <PageHeaderHeading
          title="System Administrators"
          subtitle="Manage user accounts, link employees, and assign security roles."
        />
        <PageHeaderActions>
          {hasPermission("user create") && (
            <Button onClick={() => handleOpenModal()} className="h-10 px-4">
              <PlusCircle className="mr-2 h-4 w-4" />
              Add New User
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard className="animate-in fade-in slide-in-from-top-4 duration-500 delay-75">
        <FilterSearch
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search administrators by username or name..."
        />
      </FilterCard>

      <TableCard className="animate-in fade-in slide-in-from-bottom-8 duration-700 delay-150">
        <DataTable
          columns={columns}
          data={users}
          isLoading={isLoadingUsers}
          onRowClick={
            hasPermission("user update")
              ? (user) => handleOpenModal(user)
              : undefined
          }
          pagination={pagination}
          onPaginationChange={setPagination}
          pageCount={pageCount}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          getRowId={(row) => row.id}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total Users: {totalCount}
              </span>
            </div>
          }
        />
      </TableCard>

      {/* User Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl p-0 overflow-hidden flex flex-col gap-0 border shadow-2xl bg-background">
          <DialogHeader className="px-8 py-6 border-b shrink-0 bg-muted/20">
            <DialogTitle className="text-2xl font-bold tracking-tight text-foreground">
              {isEdit ? "Edit User Account" : "Create New User Account"}
            </DialogTitle>
            <p className="text-muted-foreground text-sm mt-1">
              {isEdit
                ? "Update user details, password, or system role."
                : "Create a new user to access the system."}
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-4">
              {!isEdit && (
                <div className="space-y-2">
                  <Label>Link to Employee</Label>
                  <ReactSelect
                    options={employeeOptions}
                    value={
                      employeeOptions.find(
                        (opt) => opt.value === formData.employee_id,
                      ) || null
                    }
                    onChange={(option) =>
                      setFormData({
                        ...formData,
                        employee_id: option ? option.value : "",
                      })
                    }
                    placeholder="Select an employee..."
                    isClearable
                  />
                  <p className="text-[10px] text-muted-foreground italic">
                    Note: Only employees without a login account are shown.
                  </p>
                </div>
              )}

              {!isEdit && (
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input
                    placeholder="Enter unique username"
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                  />
                </div>
              )}

              {!isEdit && (
                <div className="space-y-2">
                  <Label>Initial Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Set account password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {!isEdit && (
                <div className="flex items-center space-x-2 py-2">
                  <Switch
                    id="temp-pass"
                    checked={formData.is_temp_pass}
                    onCheckedChange={(val) =>
                      setFormData({ ...formData, is_temp_pass: val })
                    }
                  />
                  <Label htmlFor="temp-pass" className="text-sm font-medium">
                    Require password change on first login
                  </Label>
                </div>
              )}

              <div className="space-y-2">
                <Label>System Role</Label>
                <ReactSelect
                  options={roleOptions}
                  value={
                    roleOptions.find(
                      (opt) => opt.value === String(formData.role_id),
                    ) || null
                  }
                  onChange={(option) =>
                    setFormData({
                      ...formData,
                      role_id: option ? option.value : "",
                    })
                  }
                  placeholder="Select a role..."
                  isClearable
                />
              </div>

              <div className="space-y-2">
                <Label>Account Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(val) =>
                    setFormData({ ...formData, status: val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="p-6 bg-muted/30 border-t shrink-0">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveUserMutation.mutate(formData)}
              disabled={
                saveUserMutation.isPending ||
                (!isEdit &&
                  (!formData.username ||
                    !formData.password ||
                    !formData.employee_id))
              }
              className="min-w-[100px]"
            >
              {saveUserMutation.isPending ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : null}
              {isEdit ? "Update User" : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden border shadow-2xl bg-background">
          <DialogHeader className="px-6 py-5 border-b shrink-0 bg-muted/20">
            <DialogTitle className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-600" />
              Change Password
            </DialogTitle>
          </DialogHeader>

          <div className="p-6 space-y-4">
            <div className="bg-amber-50 rounded-md p-3 text-amber-800 text-sm flex gap-2">
              <div className="font-semibold shrink-0">User:</div>
              <div>
                {selectedUserForPassword?.username} (
                {selectedUserForPassword?.employee?.full_name})
              </div>
            </div>

            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                placeholder="Manager password for authorization"
                value={passwordFormData.current_password}
                onChange={(e) =>
                  setPasswordFormData({
                    ...passwordFormData,
                    current_password: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                placeholder="Enter new strong password"
                value={passwordFormData.password}
                onChange={(e) =>
                  setPasswordFormData({
                    ...passwordFormData,
                    password: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                placeholder="Re-enter new password"
                value={passwordFormData.password_confirmation}
                onChange={(e) =>
                  setPasswordFormData({
                    ...passwordFormData,
                    password_confirmation: e.target.value,
                  })
                }
              />
            </div>
          </div>

          <DialogFooter className="p-6 bg-muted/30 border-t shrink-0">
            <Button
              variant="outline"
              onClick={() => setIsPasswordModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => changePasswordMutation.mutate(passwordFormData)}
              disabled={
                changePasswordMutation.isPending ||
                !passwordFormData.password ||
                passwordFormData.password !==
                passwordFormData.password_confirmation
              }
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {changePasswordMutation.isPending ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : null}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the login account for "
              {userToDelete?.username}". The employee record will remain, but
              they will no longer be able to log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteUserMutation.mutate(userToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteUserMutation.isPending ? (
                <Spinner className="mr-2 h-3 w-3" />
              ) : null}
              Delete Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showLogoutPopup} onOpenChange={setShowLogoutPopup}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>User Settings Updated</AlertDialogTitle>
            <AlertDialogDescription>
              User settings have been successfully updated. You must log out and
              log in again for the changes to take effect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleLogout}>
              Log Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
